import { useEffect, useMemo, useRef, useState } from 'react'
import {
  generateIntelligentTripSuggestions,
  type IntelligentTripSuggestionEnvelope,
  type IntelligentTripSuggestionRequest,
} from 'tripPlanner/api/intelligentTripSuggestionsClient'

interface IntelligentTripSuggestionHookOptions {
  activeTripDateISO?: string
  coordinates?: { lat: number, lng: number }
  placeBias?: string
  lastKnownPlace?: string
  homeAirportCode?: string
  minChars?: number
  debounceMs?: number
}

export function useIntelligentTripSuggestions(query: string, options: IntelligentTripSuggestionHookOptions) {
  const {
    activeTripDateISO,
    coordinates,
    placeBias,
    lastKnownPlace,
    homeAirportCode,
    minChars = 3,
    debounceMs = 300,
  } = options

  const [envelope, setEnvelope] = useState<IntelligentTripSuggestionEnvelope | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const value = query?.trim() ?? ''
  const timer = useRef<number | null>(null)
  const controller = useRef<AbortController | null>(null)

  useEffect(() => {
    if (value.length < minChars) {
      setEnvelope(null)
      setLoading(false)
      setError(null)
      if (controller.current) controller.current.abort()
      return
    }

    if (timer.current) {
      window.clearTimeout(timer.current)
      setEnvelope(null)
    }

    setLoading(true)
    setError(null)

    timer.current = window.setTimeout(async() => {
      try {
        if (controller.current) controller.current.abort()
        controller.current = new AbortController()

        const payload: IntelligentTripSuggestionRequest = {
          userInput: value,
          context: {
            activeTripDate: activeTripDateISO,
            coordinates,
            placeBias,
            lastKnownPlace,
            homeAirportCode,
          },
        }

        const res = await generateIntelligentTripSuggestions(payload, controller.current.signal)
        setEnvelope(res)
        setLoading(false)
      } catch(e: unknown) {
        if ((e as Error)?.name !== 'AbortError') {
          setError(e)
          setLoading(false)
        }
      }
    }, debounceMs)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [value, activeTripDateISO, coordinates, placeBias, lastKnownPlace, homeAirportCode, debounceMs, minChars])

  const suggestions: Array<string> = useMemo(() => {
    const data = envelope?.result
    if (!data) return []
    if (Array.isArray(data.placeName)) return data.placeName
    if (typeof data.placeName === 'string' && data.placeName.trim().length > 0) return [data.placeName]
    if (data.shortActivityDescription) return [data.shortActivityDescription]
    return []
  }, [envelope])

  return {
    data: envelope?.result ?? null,
    options: envelope?.options ?? [],
    suggestions,
    loading,
    error,
  }
}
